import {
  DEFAULT_LANGUAGE,
  LOCAL_STORAGE_KEY,
  PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY,
  PREFERRED_LANGUAGE_USER_SELECTED_KEY,
  SUPPORTED_LANGUAGES,
} from "utilities/Constant";
import type { BorrowerPersonalInformation } from "pages/dashboard/home/dashboardService";

export function extractDealServicePreferredLanguage(data?: BorrowerPersonalInformation): string | null {
  const preferredLanguage = data?.preferredLanguage;

  const value = preferredLanguage?.languageType;

  return value?.trim() ? value.trim() : null;
}

export function normalizeToAppLanguage(value?: string | null): string {
  if (!value?.trim()) {
    return DEFAULT_LANGUAGE;
  }

  switch (value.trim().toLowerCase()) {
    case "english":
      return "English";
    case "spanish":
    case "español":
    case "espanol":
      return "Español";
    default:
      return SUPPORTED_LANGUAGES.includes(value) ? value : DEFAULT_LANGUAGE;
  }
}

export function toUserDbPreferredLanguage(appLanguage: string): string {
  return normalizeToAppLanguage(appLanguage) === "Español" ? "Spanish" : "English";
}

export function getPreferredLanguage(reduxLanguage?: string | null): string {
  if (reduxLanguage?.trim()) {
    return normalizeToAppLanguage(reduxLanguage);
  }

  return DEFAULT_LANGUAGE;
}

export function isSpanishLanguage(value?: string | null): boolean {
  return normalizeToAppLanguage(value) === "Español";
}

export function resolvePersistedPreferredLanguage(savedLanguage?: string | null, fallbackLanguage?: string | null): string | undefined {
  if (savedLanguage?.trim()) {
    return normalizeToAppLanguage(savedLanguage);
  }

  if (fallbackLanguage?.trim()) {
    return normalizeToAppLanguage(fallbackLanguage);
  }

  return undefined;
}

export function hasExplicitUserLanguageSelection(): boolean {
  return globalThis.localStorage.getItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY) === "true";
}

export function markExplicitUserLanguageSelection(): void {
  globalThis.localStorage.setItem(PREFERRED_LANGUAGE_USER_SELECTED_KEY, "true");
}

export function getExplicitUserLanguage(): string | null {
  if (!hasExplicitUserLanguageSelection()) {
    return null;
  }

  const saved = globalThis.localStorage.getItem(LOCAL_STORAGE_KEY);
  return saved?.trim() ? normalizeToAppLanguage(saved) : null;
}

export function hasPreferredLanguageDealSyncCompleted(): boolean {
  return globalThis.sessionStorage.getItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY) === "true";
}

export function markPreferredLanguageDealSyncCompleted(): void {
  globalThis.sessionStorage.setItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY, "true");
}

export function clearPreferredLanguageDealSyncCompleted(): void {
  globalThis.sessionStorage.removeItem(PREFERRED_LANGUAGE_DEAL_SYNC_COMPLETED_KEY);
}

export function buildSyncPreferredLanguageRequest(
  userId: string,
  appLanguage: string,
  options: { isMyLoansPage: boolean; nhDealApplicationId?: string },
): { userId: string; preferredLanguage: string; nhDealApplicationId?: string } {
  const preferredLanguage = toUserDbPreferredLanguage(appLanguage);
  const nhDealApplicationId =
    !options.isMyLoansPage && options.nhDealApplicationId?.trim()
      ? options.nhDealApplicationId.trim()
      : undefined;

  return {
    userId,
    preferredLanguage,
    ...(nhDealApplicationId ? { nhDealApplicationId } : {}),
  };
}
